import { useEffect } from 'react';
import { supabase } from '../../supabase';
import { clearLastRoute } from '../../utils/ui/lastRouteStorage';
import { runAfterAuthHandoff } from '../../ui/splash/authHandoffHold';

/**
 * Role-change realtime reaction: subscribes to the user's OWN tenant_memberships
 * row. On UPDATE where role drifted or is_active flipped false: signs out
 * immediately so the stale JWT claim clears without waiting up to 1h for token
 * refresh (the set-member-role edge fn's session revoke is the server backstop).
 * RLS self-read OK (tenant_memberships_select_self_or_same_tenant:
 * user_id = auth.uid()). Channel failures are silently non-fatal.
 *
 * Extracted verbatim from AuthContext (500-line cap). `signOut` must be the
 * CONTEXT signOut (cache wipes run in its finally), never raw auth.signOut.
 */
export function useMembershipRoleWatch({
  sessionUserId, tenantId, loading, tenantLoading, role, signOut, setAuthNotice,
}) {
  useEffect(() => {
    if (!sessionUserId || !tenantId || loading || tenantLoading) return;
    const knownRole = role;
    let channel = null;
    // Realtime setup waits out a sign-in handoff (authHandoffHold.js); the
    // deactivation poll + server gates cover revocation meanwhile.
    const cancelDeferred = runAfterAuthHandoff(() => {
      channel = supabase
        .channel(`membership-role-watch-${sessionUserId}`, { config: { private: true } })
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'tenant_memberships',
            filter: `user_id=eq.${sessionUserId}`,
          },
          (payload) => {
            const row = payload.new;
            const roleDrifted = row?.role && knownRole && row.role !== knownRole;
            const deactivated = row?.is_active === false;
            if (roleDrifted || deactivated) {
              clearLastRoute();
              // Context signOut nulls authNotice at its start, so set the
              // role-change notice AFTER it completes.
              void (async () => {
                await signOut();
                setAuthNotice({ type: 'info', key: 'auth.notices.roleChanged' });
              })();
            }
          },
        )
        .subscribe();
    }, { first: true });
    return () => { cancelDeferred(); if (channel) void supabase.removeChannel(channel); };
  }, [sessionUserId, tenantId, loading, tenantLoading, role, signOut, setAuthNotice]);
}
